<i18n
  src="@/locales/views/organization/partners/producers/list_producers.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/producer.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>
<template>
  <div v-if="load_data_general">
    <div class="mt-8 flex flex-row justify-between">
      <div class="panel-plain-label space-x-2">
        <badge-default
          :label="total_producers + ' ' + $t('labels.total_number_producers')"
        />
        <badge-default
          :label="total_hectares + ' ' + $t('labels.total_hectares')"
        />
        <badge-default
          :label="total_square_meters + ' ' + $t('labels.total_square_meters')"
        />
      </div>

      <div>
        <button
          v-if="producers.length > 0"
          type="button"
          @click.prevent="downloadRepCropFieldsXlsx()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              downloading_crop_field ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          ></i
          ><span class="space-icon"></span
          >{{ $t("labels.download_crop_fields_report_button") }}
        </button>
        <span class="space-actions" />
        <button
          v-if="producers.length > 0"
          type="button"
          @click.prevent="downloadRepProducersXlsx()"
          class="btn-white"
        >
          <i
            :class="[
              'fa-solid',
              downloading ? 'fa-sync fa-spin' : 'fa-file-excel'
            ]"
          ></i
          ><span class="space-icon"></span
          >{{ $t("labels.download_producers_report_button") }}
        </button>
      </div>
    </div>
    <div class="mt-8 flex flex-col">
      <ag-grid-vue
        v-if="producers.length > 0"
        style="width: 100%; height: 550px"
        class="ag-theme-alpine w-full h-full"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="producers"
        @grid-ready="onGridReady"
      >
      </ag-grid-vue>
      <alert-warning
        class="mt-8"
        v-else
        :warning_data="{
          title: $t('labels.no_producers_found')
        }"
      />
    </div>
  </div>

  <loading-message v-else />

  <table-paginator
    v-if="load_data_general"
    v-model="current_page"
    :total_rows="total_producers"
  />
</template>

<script>
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue3";
import TablePaginator from "@/components/views/layout/TablePaginator.vue";
import Requester from "@/views/organization/requesters/partners/producers/list_producers.js";
import GoToLink from "@/components/views/layout/ag_grid/GoToLink.vue";

export default {
  name: "OrgPartnersProducersListProducersInactive",
  components: { AgGridVue, TablePaginator },
  data() {
    return {
      load_data_general: false,
      downloading: false,
      downloading_crop_field: false,
      producers: this.producers_inactive,
      total_hectares: 0,
      total_square_meters: 0,
      current_page: 1,
      total_producers: 0,
      rows_per_page: 10,
      defaultColDef: {
        sortable: true,
        filter: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.$t(
            "model.producer.organization_producer.producer_code"
          ),
          field: "organization_producer.producer_code",
          filter: true,
          cellRendererSelector: params => {
            return {
              component: GoToLink,
              params: {
                text: params.value,
                path_name: "org_partners_producers_show_producer_path",
                params_path: {
                  organization_id: this.organization_id,
                  producer_id: params.data.id
                }
              }
            };
          }
        },
        {
          headerName: this.$t("model.producer.producer_code"),
          field: "producer_code",
          filter: true,
          cellRendererSelector: params => {
            return {
              component: GoToLink,
              params: {
                text: params.value,
                path_name: "org_partners_producers_show_producer_path",
                params_path: {
                  organization_id: this.organization_id,
                  producer_id: params.data.id
                }
              }
            };
          }
        },
        {
          headerName: this.$t("model.producer.lastname"),
          field: "lastname",
          filter: true,
          width: 300
        },
        {
          headerName: this.$t("model.producer.name"),
          field: "name",
          filter: true,
          width: 300
        },
        {
          headerName: this.$t("model.producer.producer_crops"),
          filter: true,
          valueGetter: params => {
            return params.data.producer_crops
              .map(pc => pc.crop.name)
              .join(", ");
          }
        },
        {
          headerName: this.$t("model.producer.organization_crop_fields.crop_field_code"),
          filter: true,
          valueGetter: params => {
            return params.data.organization_crop_field_codes
              .join(", ");
          }
        },
        {
          headerName: this.$t("model.crop_field.hectares"),
          field: "hectares",
          filter: true,
          valueFormatter: params => {
            return this.numberWithPrecision(params.value);
          }
        },
        {
          headerName: this.$t("model.crop_field.square_meters"),
          field: "square_meters",
          filter: true,
          valueFormatter: params => {
            return this.numberWithPrecision(params.value);
          }
        },
        {
          headerName: this.$t(
            "model.producer.organization_producer.formalized"
          ),
          field: "organization_producer.formalized",
          filter: true,
          cellRendererSelector: params => {
            return {
              component: {
                template: `<yes-no-label :value="${params.value}"></yes-no-label>`
              }
            };
          }
        }
      ]
    };
  },
  props: {
    organization_id: {
      required: true
    },
    producers_inactive: null
  },
  watch: {
    current_page: async function (current_page) {
      await this.fetchProducers();
      this.$emit("update:producers_inactive", current_page);
    }
  },
  methods: {
    fetchProducers: async function () {
      let response = await Requester.fetchProducersInactive(
        this.organization_id,
        this.current_page,
        this.rows_per_page
      );
      this.producers = response.data.producers;
      this.total_producers = response.data.total;
      this.total_hectares =
        this.numberWithPrecision(response.data.total_hectares) || 0;
      this.total_square_meters =
        this.numberWithPrecision(response.data.total_square_meters) || 0;
      this.$emit("update:producers_formalized_active", this.producers);
      this.load_data_general = true;
    },
    showProducer: function (producer) {
      this.$router.push({
        name: "org_partners_producers_show_producer_path",
        params: {
          organization_id: this.organization_id,
          producer_id: producer.id
        }
      });
    },
    producerCrops: function (producer) {
      return producer.producer_crops
        .map(producer_crop => producer_crop.crop.name)
        .join(", ");
    },
    downloadRepProducersXlsx: async function () {
      this.downloading = true;
      try {
        var response = await Requester.downloadRepProducersInactiveXlsx(
          this.organization_id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute("download", "Productores_inactivos.xlsx");
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        error;
      }
      this.downloading = false;
    },
    downloadRepCropFieldsXlsx: async function () {
      this.downloading_crop_field = true;
      try {
        var response = await Requester.downloadRepCropFieldsInactiveXlsx(
          this.organization_id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          "Productores_inactivos_y_campos.xlsx"
        );
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        error;
      }
      this.downloading_crop_field = false;
    },
    sizeToFit() {
      this.gridApi.sizeColumnsToFit({
        defaultMinWidth: 150
      });
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.sizeToFit();
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => this.fetchProducers())
    );
  }
};
</script>
